import { useEffect, useRef } from 'react';

import { getFileViewerType } from '../lib/getFileViewerType';
import { useImageRotation } from '../lib/useImageRotation';
import { FileViewerSwitch } from './FileViewerSwitch';
import { ThumbnailStrip } from './ThumbnailStrip';
import { ViewerNavArrow } from './ViewerNavArrow';
import { ViewerToolbar } from './ViewerToolbar';
import { getFileExtension } from '@/lib/getFileExtension';
import { FileEntity } from '@/shared/interfaces';

type InlineFileViewerProps = {
    files: FileEntity[];
    currentIndex: number;
    onNavigate: (index: number) => void;
    onClose: () => void;
};

export const InlineFileViewer = ({
    files,
    currentIndex,
    onNavigate,
    onClose,
}: InlineFileViewerProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const fileEntity = files[currentIndex];
    const fileUrl = `${window.location.origin}/api/files/${fileEntity.guid}`;
    const extension = getFileExtension(fileEntity.fileName);
    const viewerType = getFileViewerType(extension);
    const { rotation, rotateLeft, rotateRight } =
        useImageRotation(currentIndex);

    const canRotate = viewerType === 'image' || viewerType === 'video';

    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < files.length - 1;

    const handleFullscreen = () => {
        contentRef.current?.requestFullscreen?.();
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft' && hasPrev) onNavigate(currentIndex - 1);
            if (e.key === 'ArrowRight' && hasNext) onNavigate(currentIndex + 1);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, hasPrev, hasNext, onClose, onNavigate]);

    return (
        <div
            className="fixed inset-0 z-50 flex flex-col bg-black/85"
            role="dialog"
            aria-modal="true"
            aria-label={fileEntity.fileName}
        >
            <ViewerToolbar
                fileName={fileEntity.fileName}
                fileUrl={fileUrl}
                onClose={onClose}
                onFullscreen={handleFullscreen}
                onRotateLeft={canRotate ? rotateLeft : undefined}
                onRotateRight={canRotate ? rotateRight : undefined}
            />

            {/* Content area with side nav arrows */}
            <div
                className="relative flex min-h-0 flex-1 items-center justify-center"
                ref={contentRef}
            >
                {files.length > 1 && (
                    <ViewerNavArrow
                        direction="left"
                        disabled={!hasPrev}
                        onClick={() => onNavigate(currentIndex - 1)}
                    />
                )}

                <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-auto px-16">
                    <FileViewerSwitch
                        viewerType={viewerType}
                        fileUrl={fileUrl}
                        fileName={fileEntity.fileName}
                        fileSizeBytes={
                            fileEntity.fileSizeBytes ??
                            fileEntity.file?.size ??
                            0
                        }
                        rotation={rotation}
                    />
                </div>

                {files.length > 1 && (
                    <ViewerNavArrow
                        direction="right"
                        disabled={!hasNext}
                        onClick={() => onNavigate(currentIndex + 1)}
                    />
                )}
            </div>

            {files.length > 1 && (
                <ThumbnailStrip
                    files={files}
                    currentIndex={currentIndex}
                    onSelect={onNavigate}
                />
            )}
        </div>
    );
};
